import { Form, Input, notification } from 'antd';
import Button from 'components/atoms/Button';
import { Col, Container, Row } from 'components/atoms/Grid';
import Link from 'components/atoms/Link';
import Text from 'components/atoms/Text';
import Colors from 'components/tokens/Colors';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import ContextoLogo from './assets/contexto-logo.svg';
import { useMutation, useLazyQuery } from '@apollo/client';
import { gql } from 'apollo-boost';
import { contextClient } from 'index';
import { UserContext } from 'contexts';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const login_query = gql`
    mutation ($email: String!, $password: String!) {
        doSignIn(email: $email, password: $password) {
            token
            redirectUri
        }
    }
`;

const forget_password_query = gql`
    mutation ($email: String!) {
        askPassword(email: $email)
    }
`;

const get_user_query = gql`
    query {
        UserItem {
            name
            email
            gender
            birthdate
            documentNumber
            signature_start_date
            signature_end_date
            phone
            active_plan
            payment_method
            token
            payment_asked_price
            payment_custom_price
            location {
                city
                state
                cep
                street
                number
                neighborhood
                service
                complement
            }
            friends {
                name
                _id
                email
            }
        }
    }
`;

export default function LoginTemplate() {
    const [form] = Form.useForm();
    const { setUser, setToken, resetUser } = useContext(UserContext);
    const navigate = useNavigate();
    const { state } = useLocation();
    const [isError, setIsError] = useState(false);
    const [isErrorForget, setIsErrorForget] = useState(false);

    const [doSignIn, { loading }] = useMutation(login_query, {
        client: contextClient
    });

    const [doForgetPassword, { loading: loadingForget }] = useMutation(forget_password_query, {
        client: contextClient
    });

    const [getUser] = useLazyQuery(get_user_query, {
        client: contextClient
    });

    useEffect(() => {
        if (state?.logout) {
            resetUser();
        }
    }, [resetUser, state]);

    const submitForm = values => {
        doSignIn({
            variables: values
        })
            .then(response => {
                if (response?.data?.doSignIn?.token) {
                    getUser({
                        context: { headers: { 'x-access-token': response?.data?.doSignIn?.token } }
                    })
                        .then(res => {
                            localStorage.setItem('LP_user', JSON.stringify(res.data.UserItem));
                            setUser(res.data.UserItem);
                            navigate(`/${response?.data?.doSignIn?.redirectUri.split('/')[3]}`);
                            localStorage.setItem('LP_token', JSON.stringify(response?.data?.doSignIn?.token));
                            setToken(response?.data?.doSignIn?.token);
                        })
                        .catch(err => {
                            console.error(err);
                        });
                }
            })
            .catch(err => {
                console.error('err', err);
                setIsError(true);
            });
    };

    const forgetPassword = () => {
        const email = form.getFieldValue('email');
        if (email) {
            doForgetPassword({ variables: { email } })
                .then(() =>
                    notification.success({
                        message: 'Foi enviado ao e-mail informado instruções para recuperação de senha.',
                        placement: 'topRight'
                    })
                )
                .catch(err => {
                    notification.error({
                        message: 'Ocorreu um erro, por favor tente revise o e-mail informado ou atualize a página.',
                        placement: 'topRight'
                    });
                    console.error(err);
                });
        } else {
            setIsErrorForget('Para recuperar sua senha, informe seu e-mail.');
        }
    };

    return (
        <>
            <Helmet>
                <title>Lupa | Contexto</title>
            </Helmet>
            <MainContent fluid>
                <Container px="0.5 1">
                    <Row pb="8 12" pt="8 12">
                        <Col size="hidden 1" />
                        <Col size="12 4" mb="1 0" pl="1 3">
                            <Row>
                                <Col size="12" mb="4 25">
                                    <StyledImg src={ContextoLogo} />
                                    <Text title size="32 48" lineHeight="30 40" color={Colors.white}>
                                        Entre na sua conta
                                    </Text>
                                </Col>
                                <Col size="hidden auto">
                                    <Text body2 color={Colors.white}>
                                        {'Ainda não é membro? '}
                                    </Text>
                                    <Link href="/contexto" color={Colors.white}>
                                        Veja os planos.
                                    </Link>
                                </Col>
                            </Row>
                        </Col>
                        <Col size="hidden 1" />
                        <Col size="12 4" pr="1 3">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={submitForm}
                                onValuesChange={() => {
                                    setIsError(false);
                                    setIsErrorForget(false);
                                }}
                            >
                                <Form.Item
                                    label="E-mail"
                                    name="email"
                                    hasFeedback={loading || isError || isErrorForget}
                                    validateStatus={
                                        isError || isErrorForget ? 'error' : loading ? 'validating' : undefined
                                    }
                                    help={
                                        <Text body3 highlight color={Colors.red}>
                                            {isErrorForget}
                                        </Text>
                                    }
                                    style={{ marginBottom: 24 }}
                                >
                                    <Input type="email" placeholder="exemplo@mail.com" />
                                </Form.Item>
                                <Form.Item
                                    label="Senha"
                                    name="password"
                                    hasFeedback={isError}
                                    validateStatus={isError ? 'error' : undefined}
                                    help={
                                        <Text
                                            style={{ cursor: isError ? 'default' : 'pointer' }}
                                            onClick={() => (!isError && !loadingForget ? forgetPassword() : null)}
                                            body3
                                            highlight={isError}
                                            color={isError ? Colors.red : Colors.white}
                                        >
                                            {isError ? 'Usuário e/ou senha inválido(s)!' : 'Esqueceu sua senha?'}
                                        </Text>
                                    }
                                    style={{ marginBottom: 24 }}
                                >
                                    <Input.Password placeholder="********" />
                                </Form.Item>
                                <Button primary disabled={loading}>
                                    Entrar
                                </Button>
                            </Form>
                        </Col>
                    </Row>
                </Container>
            </MainContent>
        </>
    );
}

const MainContent = styled(Container)`
    background-color: ${Colors.black};
    min-height: calc(100vh - 80px);

    label {
        color: ${Colors.white};
    }

    input,
    .ant-input-affix-wrapper > input.ant-input {
        padding: 15px;
        border-radius: 8px;
    }

    .ant-input-affix-wrapper {
        border-radius: 8px;
    }

    .ant-form-item-explain.ant-form-item-explain-connected {
        margin-top: 16px;
    }
`;

const StyledImg = styled.img`
    position: relative;
    width: 82px;
    display: block;
    margin-bottom: 16px;

    @media (min-width: 768px) {
        width: 112px;
    }
`;
